import { create } from 'zustand';
import {
  activateDraw,
  activateStamp,
  consumeConnect,
  consumeDraw,
  cycleConnect,
  cycleDraw,
  cycleStamp,
  emptyArmed,
  type ConnectMode,
} from '../utils/drawTools';

interface ToastState {
  message: string;
  id: number;
}

type Updater<T> = T | ((prev: T) => T);

export type CanvasDrawTool = 'none' | 'frame' | 'box' | 'arrow' | 'line' | 'text';

interface UIStore {
  // Panel visibility
  isToolboxOpen: boolean;
  sidebarTab: 'palette' | 'info' | 'pages';
  isJsonPanelOpen: boolean;
  isLayerPanelOpen: boolean;
  isSettingsOpen: boolean;
  isFeedbackOpen: boolean;
  isDevStateViewerOpen: boolean;
  isProjectManagerOpen: boolean;
  isChordModalOpen: boolean;
  isPngExportModalOpen: boolean;
  isMermaidModalOpen: boolean;
  isPageManagerOpen: boolean;
  isDistractionFree: boolean;
  isHeaderVisible: boolean;
  isToolbarExpanded: boolean;
  openDropdown: 'new' | 'file' | 'tools' | null;
  
  // Info panel
  infoPanelNodeId: string | null;
  infoPanelParticipantId: string | null;
  infoPanelMessageId: string | null;
  infoPanelFragmentId: string | null;
  
  // Toast
  toast: ToastState | null;
  
  // Hover state
  hoveredNodeId: string | null;
  hoveredParticipantId: string | null;
  hoveredMessageId: string | null;
  
  // Frame / markup draw mode
  isFrameDrawMode: boolean;
  canvasDrawTool: CanvasDrawTool;
  canvasStampType: string | null;
  /** False = one placement then the tool turns off. True = keep drawing. */
  canvasToolSticky: boolean;
  canvasConnectMode: ConnectMode;
  isCommandPaletteOpen: boolean;
  
  // Discovery mode
  discoveryMode: boolean;
  
  // Actions
  setToolboxOpen: (open: Updater<boolean>) => void;
  toggleToolbox: () => void;
  setSidebarTab: (tab: 'palette' | 'info' | 'pages') => void;
  setJsonPanelOpen: (open: Updater<boolean>) => void;
  setLayerPanelOpen: (open: Updater<boolean>) => void;
  setSettingsOpen: (open: Updater<boolean>) => void;
  setFeedbackOpen: (open: Updater<boolean>) => void;
  setDevStateViewerOpen: (open: Updater<boolean>) => void;
  setProjectManagerOpen: (open: Updater<boolean>) => void;
  setChordModalOpen: (open: Updater<boolean>) => void;
  setPngExportModalOpen: (open: Updater<boolean>) => void;
  setMermaidModalOpen: (open: Updater<boolean>) => void;
  setPageManagerOpen: (open: Updater<boolean>) => void;
  setDistractionFree: (enabled: Updater<boolean>) => void;
  setHeaderVisible: (visible: Updater<boolean>) => void;
  /** Restore persisted title-bar visibility for a project (default: shown). */
  syncHeaderForProject: (projectId: string | null) => void;
  /** Show the title bar and clear a persisted dismiss for the current project. */
  revealHeader: () => void;
  setToolbarExpanded: (expanded: Updater<boolean>) => void;
  setOpenDropdown: (dropdown: Updater<'new' | 'file' | 'tools' | null>) => void;
  setInfoPanelNodeId: (nodeId: string | null) => void;
  setInfoPanelParticipantId: (participantId: string | null) => void;
  setInfoPanelMessageId: (messageId: string | null) => void;
  setInfoPanelFragmentId: (fragmentId: string | null) => void;
  showToast: (message: string) => void;
  clearToast: () => void;
  setHoveredNodeId: (nodeId: string | null) => void;
  setHoveredParticipantId: (participantId: string | null) => void;
  setHoveredMessageId: (messageId: string | null) => void;
  setFrameDrawMode: (mode: Updater<boolean>) => void;
  setCanvasDrawTool: (tool: Updater<CanvasDrawTool>) => void;
  setCanvasStampType: (type: string | null) => void;
  cycleDrawTool: (tool: CanvasDrawTool) => void;
  cycleStampType: (type: string) => void;
  activateDrawTool: (tool: CanvasDrawTool, sticky: boolean) => void;
  activateStampType: (type: string, sticky: boolean) => void;
  cycleConnectMode: () => void;
  clearArmedTools: () => void;
  consumeOneShotDraw: () => void;
  consumeOneShotConnect: () => void;
  setCommandPaletteOpen: (open: Updater<boolean>) => void;
  setDiscoveryMode: (mode: Updater<boolean>) => void;
  
  // Close all panels
  closeAllPanels: () => void;
}

const HEADER_HIDDEN_KEY = 'velostruct-header-hidden';
/** Same key as projectStore — used to hydrate visibility before React mounts. */
const ACTIVE_PROJECT_KEY = 'velostruct-active-project';

function readHeaderHidden(): Record<string, true> {
  try {
    const raw = localStorage.getItem(HEADER_HIDDEN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, true> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (value === true) out[id] = true;
    }
    return out;
  } catch {
    return {};
  }
}

function writeHeaderHidden(map: Record<string, true>) {
  try {
    localStorage.setItem(HEADER_HIDDEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

function currentProjectId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_PROJECT_KEY);
  } catch {
    return null;
  }
}

function persistHeaderHidden(projectId: string | null, hidden: boolean) {
  if (!projectId) return;
  const map = readHeaderHidden();
  if (hidden) map[projectId] = true;
  else delete map[projectId];
  writeHeaderHidden(map);
}

function isHeaderHiddenFor(projectId: string | null): boolean {
  if (!projectId) return false;
  return readHeaderHidden()[projectId] === true;
}

export const useUIStore = create<UIStore>((set, get) => ({
  // Initial state
  isToolboxOpen: true,
  sidebarTab: 'palette',
  isJsonPanelOpen: false,
  isLayerPanelOpen: true,
  isSettingsOpen: false,
  isFeedbackOpen: false,
  isDevStateViewerOpen: false,
  isProjectManagerOpen: false,
  isChordModalOpen: false,
  isPngExportModalOpen: false,
  isMermaidModalOpen: false,
  isPageManagerOpen: false,
  isDistractionFree: false,
  isHeaderVisible: !isHeaderHiddenFor(currentProjectId()),
  isToolbarExpanded: false,
  openDropdown: null,
  infoPanelNodeId: null,
  infoPanelParticipantId: null,
  infoPanelMessageId: null,
  infoPanelFragmentId: null,
  toast: null,
  hoveredNodeId: null,
  hoveredParticipantId: null,
  hoveredMessageId: null,
  isFrameDrawMode: false,
  canvasDrawTool: 'none',
  canvasStampType: null,
  canvasToolSticky: false,
  canvasConnectMode: 'none',
  isCommandPaletteOpen: false,
  discoveryMode: false,

  // Actions
  setToolboxOpen: (open) => set((state) => ({ isToolboxOpen: typeof open === 'function' ? open(state.isToolboxOpen) : open })),
  toggleToolbox: () => set((state) => ({ isToolboxOpen: !state.isToolboxOpen })),
  setSidebarTab: (tab) => set({ sidebarTab: tab }),
  setJsonPanelOpen: (open) => set((state) => ({ isJsonPanelOpen: typeof open === 'function' ? open(state.isJsonPanelOpen) : open })),
  setLayerPanelOpen: (open) => set((state) => ({ isLayerPanelOpen: typeof open === 'function' ? open(state.isLayerPanelOpen) : open })),
  setSettingsOpen: (open) => set((state) => ({ isSettingsOpen: typeof open === 'function' ? open(state.isSettingsOpen) : open })),
  setFeedbackOpen: (open) => set((state) => ({ isFeedbackOpen: typeof open === 'function' ? open(state.isFeedbackOpen) : open })),
  setDevStateViewerOpen: (open) => set((state) => ({ isDevStateViewerOpen: typeof open === 'function' ? open(state.isDevStateViewerOpen) : open })),
  setProjectManagerOpen: (open) => set((state) => ({ isProjectManagerOpen: typeof open === 'function' ? open(state.isProjectManagerOpen) : open })),
  setChordModalOpen: (open) => set((state) => ({ isChordModalOpen: typeof open === 'function' ? open(state.isChordModalOpen) : open })),
  setPngExportModalOpen: (open) => set((state) => ({ isPngExportModalOpen: typeof open === 'function' ? open(state.isPngExportModalOpen) : open })),
  setMermaidModalOpen: (open) => set((state) => ({ isMermaidModalOpen: typeof open === 'function' ? open(state.isMermaidModalOpen) : open })),
  setPageManagerOpen: (open) => set((state) => ({ isPageManagerOpen: typeof open === 'function' ? open(state.isPageManagerOpen) : open })),
  setDistractionFree: (enabled) => set((state) => ({ isDistractionFree: typeof enabled === 'function' ? enabled(state.isDistractionFree) : enabled })),
  setHeaderVisible: (visible) => set((state) => {
    const next = typeof visible === 'function' ? visible(state.isHeaderVisible) : visible;
    persistHeaderHidden(currentProjectId(), !next);
    return { isHeaderVisible: next };
  }),
  syncHeaderForProject: (projectId) => set({ isHeaderVisible: !isHeaderHiddenFor(projectId) }),
  revealHeader: () => {
    persistHeaderHidden(currentProjectId(), false);
    set({ isHeaderVisible: true });
  },
  setToolbarExpanded: (expanded) => set((state) => ({ isToolbarExpanded: typeof expanded === 'function' ? expanded(state.isToolbarExpanded) : expanded })),
  setOpenDropdown: (dropdown) => set((state) => ({ openDropdown: typeof dropdown === 'function' ? dropdown(state.openDropdown) : dropdown })),
  setInfoPanelNodeId: (nodeId) => set({ infoPanelNodeId: nodeId }),
  setInfoPanelParticipantId: (participantId) => set({ infoPanelParticipantId: participantId }),
  setInfoPanelMessageId: (messageId) => set({ infoPanelMessageId: messageId }),
  setInfoPanelFragmentId: (fragmentId) => set({ infoPanelFragmentId: fragmentId }),
  
  showToast: (message) => {
    set({ toast: { message, id: Date.now() } });
    // Auto-clear after 2.5s
    setTimeout(() => {
      const current = get().toast;
      if (current && current.message === message) {
        set({ toast: null });
      }
    }, 2500);
  },
  
  clearToast: () => set({ toast: null }),
  setHoveredNodeId: (nodeId) => {
    if (get().hoveredNodeId === nodeId) return;
    set({ hoveredNodeId: nodeId });
  },
  setHoveredParticipantId: (participantId) => {
    if (get().hoveredParticipantId === participantId) return;
    set({ hoveredParticipantId: participantId });
  },
  setHoveredMessageId: (messageId) => {
    if (get().hoveredMessageId === messageId) return;
    set({ hoveredMessageId: messageId });
  },
  setFrameDrawMode: (mode) => set((state) => {
    const on = typeof mode === 'function' ? mode(state.isFrameDrawMode) : mode;
    if (on) return { isFrameDrawMode: true, canvasDrawTool: 'frame' as const, canvasStampType: null, canvasConnectMode: 'none' as const };
    const leavingFrame = state.canvasDrawTool === 'frame' || state.isFrameDrawMode;
    return {
      isFrameDrawMode: false,
      canvasDrawTool: leavingFrame ? 'none' as const : state.canvasDrawTool,
      canvasStampType: leavingFrame ? null : state.canvasStampType,
      canvasToolSticky: leavingFrame ? false : state.canvasToolSticky,
    };
  }),
  setCanvasDrawTool: (tool) => set((state) => {
    const next = typeof tool === 'function' ? tool(state.canvasDrawTool) : tool;
    return {
      canvasDrawTool: next,
      isFrameDrawMode: next === 'frame',
      canvasStampType: null,
      canvasToolSticky: false,
      canvasConnectMode: next === 'none' ? state.canvasConnectMode : 'none' as const,
    };
  }),
  setCanvasStampType: (type) => set((state) => type
    ? { canvasStampType: type, canvasDrawTool: 'none' as const, isFrameDrawMode: false, canvasConnectMode: 'none' as const }
    : { canvasStampType: null, canvasToolSticky: state.canvasDrawTool === 'none' ? false : state.canvasToolSticky }),
  cycleDrawTool: (tool) => set((state) => {
    const next = cycleDraw({
      drawTool: state.canvasDrawTool,
      stampType: state.canvasStampType,
      sticky: state.canvasToolSticky,
      connectMode: state.canvasConnectMode,
    }, tool);
    return {
      canvasDrawTool: next.drawTool,
      canvasStampType: next.stampType,
      canvasToolSticky: next.sticky,
      canvasConnectMode: next.connectMode,
      isFrameDrawMode: next.drawTool === 'frame',
    };
  }),
  cycleStampType: (type) => set((state) => {
    const next = cycleStamp({
      drawTool: state.canvasDrawTool,
      stampType: state.canvasStampType,
      sticky: state.canvasToolSticky,
      connectMode: state.canvasConnectMode,
    }, type);
    return {
      canvasDrawTool: next.drawTool,
      canvasStampType: next.stampType,
      canvasToolSticky: next.sticky,
      canvasConnectMode: next.connectMode,
      isFrameDrawMode: false,
    };
  }),
  activateDrawTool: (tool, sticky) => {
    const next = activateDraw(tool, sticky);
    set({
      canvasDrawTool: next.drawTool,
      canvasStampType: next.stampType,
      canvasToolSticky: next.sticky,
      canvasConnectMode: next.connectMode,
      isFrameDrawMode: next.drawTool === 'frame',
    });
  },
  activateStampType: (type, sticky) => {
    const next = activateStamp(type, sticky);
    set({
      canvasDrawTool: next.drawTool,
      canvasStampType: next.stampType,
      canvasToolSticky: next.sticky,
      canvasConnectMode: next.connectMode,
      isFrameDrawMode: false,
    });
  },
  cycleConnectMode: () => set((state) => {
    const next = cycleConnect({
      drawTool: state.canvasDrawTool,
      stampType: state.canvasStampType,
      sticky: state.canvasToolSticky,
      connectMode: state.canvasConnectMode,
    });
    return {
      canvasDrawTool: next.drawTool,
      canvasStampType: next.stampType,
      canvasToolSticky: next.sticky,
      canvasConnectMode: next.connectMode,
      isFrameDrawMode: false,
    };
  }),
  clearArmedTools: () => {
    const cleared = emptyArmed();
    set({
      canvasDrawTool: cleared.drawTool,
      canvasStampType: cleared.stampType,
      canvasToolSticky: cleared.sticky,
      canvasConnectMode: cleared.connectMode,
      isFrameDrawMode: false,
    });
  },
  consumeOneShotDraw: () => set((state) => {
    const next = consumeDraw({
      drawTool: state.canvasDrawTool,
      stampType: state.canvasStampType,
      sticky: state.canvasToolSticky,
      connectMode: state.canvasConnectMode,
    });
    return {
      canvasDrawTool: next.drawTool,
      canvasStampType: next.stampType,
      canvasToolSticky: next.sticky,
      isFrameDrawMode: next.drawTool === 'frame',
    };
  }),
  consumeOneShotConnect: () => set((state) => {
    const next = consumeConnect({
      drawTool: state.canvasDrawTool,
      stampType: state.canvasStampType,
      sticky: state.canvasToolSticky,
      connectMode: state.canvasConnectMode,
    });
    return { canvasConnectMode: next.connectMode };
  }),
  setCommandPaletteOpen: (open) => set((state) => ({ isCommandPaletteOpen: typeof open === 'function' ? open(state.isCommandPaletteOpen) : open })),
  setDiscoveryMode: (mode) => set((state) => ({ discoveryMode: typeof mode === 'function' ? mode(state.discoveryMode) : mode })),
  
  closeAllPanels: () => set({
    isJsonPanelOpen: false,
    isSettingsOpen: false,
    isFeedbackOpen: false,
    isDevStateViewerOpen: false,
    isProjectManagerOpen: false,
    isChordModalOpen: false,
    isPngExportModalOpen: false,
    isMermaidModalOpen: false,
    isPageManagerOpen: false,
    isCommandPaletteOpen: false,
    infoPanelNodeId: null,
    infoPanelParticipantId: null,
    infoPanelMessageId: null,
    infoPanelFragmentId: null,
  }),
}));
